/**
 * Authentication service — MSAL + Legacy dual-auth.
 *
 * Mirrors Portal-Ui's Authentication.js in TypeScript. Supports both:
 *   - SSO via MSAL (Azure AD B2C / CIAM) — acquireTokenSilent
 *   - Legacy username/password login via Identity API endpoints
 *
 * Auth API calls use inline axios instances (not httpClientFactory) to avoid
 * circular dependencies — httpClientFactory depends on this module.
 */

import axios from 'axios';
import { getConfig } from './config';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthStorageData {
  bearerToken: string;
  refreshTokenId: string;
  expiresAtUtc: string | null;
  identity?: DecodedIdentity;
}

export interface DecodedIdentity {
  userId?: number;
  email?: string;
  displayName?: string;
  roles?: string | string[];
  [key: string]: unknown;
}

export interface LoginResult {
  token: string;
  refreshTokenId: string;
  expiresAtUtc: string | null;
  identity: DecodedIdentity;
}

export interface RefreshResult {
  token: string;
  refreshTokenId: string;
  expiresAtUtc: string | null;
}

export interface WhoAmIPayload {
  is_reg?: string | boolean;
  usr_status?: string;
  cus_roles?: string | string[];
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  oid?: string;
  usr_auto_reg?: string | boolean;
  schwab_ofc_id?: string;
  [key: string]: unknown;
}

export interface WhoAmIResult {
  payload: WhoAmIPayload;
  [key: string]: unknown;
}

interface LoginError extends Error {
  status?: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AUTH_STORAGE_KEY = 'auth';

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let accessToken: string | null = null;
let refreshTokenId: string | null = null;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Strip the "Bearer " prefix if present — always store the raw JWT. */
function normalizeToken(token: string): string {
  return token.startsWith('Bearer ') ? token.slice(7) : token;
}

/**
 * Create a plain axios instance pointed at a resolved URL from config.
 * Used for auth endpoints to avoid circular deps with httpClientFactory.
 */
function authClient(configKey: 'apiBaseAuth' | 'apiBaseAuthV2' | 'apiAccountsV2'): ReturnType<typeof axios.create> {
  const cfg = getConfig();
  return axios.create({ baseURL: cfg[configKey] });
}

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

export function loadTokensFromStorage(): AuthStorageData | null {
  const stored = localStorage.getItem(AUTH_STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored) as AuthStorageData;
  } catch {
    return null;
  }
}

export function saveTokensToStorage(
  bearerToken: string,
  refreshTokenIdValue: string,
  expiresAtUtc: string | null = null,
  identity?: DecodedIdentity,
): void {
  const rawToken = normalizeToken(bearerToken);
  const authData: AuthStorageData = {
    bearerToken: rawToken,
    refreshTokenId: refreshTokenIdValue,
    expiresAtUtc,
    ...(identity ? { identity } : {}),
  };
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
  accessToken = rawToken;
  refreshTokenId = refreshTokenIdValue;
}

export function clearTokensFromStorage(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  accessToken = null;
  refreshTokenId = null;
}

/**
 * Clear both the legacy auth and the SSO session.
 *
 * Called from API interceptors after a final auth failure so the user is
 * fully signed out before being redirected back to /login (otherwise
 * AuthContext would rehydrate ssoSession on /login and bounce them back
 * into the app).
 */
export function clearAllAuth(): void {
  clearTokensFromStorage();
  localStorage.removeItem('ssoSession');
}

// ---------------------------------------------------------------------------
// Token accessors
// ---------------------------------------------------------------------------

export function getAccessToken(): string | null {
  if (accessToken) return accessToken;
  const stored = loadTokensFromStorage();
  if (stored?.bearerToken) {
    accessToken = stored.bearerToken;
    return accessToken;
  }
  return null;
}

export function getRefreshTokenId(): string | null {
  if (refreshTokenId) return refreshTokenId;
  const stored = loadTokensFromStorage();
  if (stored?.refreshTokenId) {
    refreshTokenId = stored.refreshTokenId;
    return refreshTokenId;
  }
  return null;
}

// ---------------------------------------------------------------------------
// SSO-aware token acquisition
// ---------------------------------------------------------------------------

/**
 * Get a valid access token — tries MSAL (SSO) first, then falls back to
 * the legacy token in storage.
 *
 * @param scope - MSAL scope (e.g. `api://<clientId>/ApiUse`). Optional for
 *                legacy-only setups; defaults to `getConfig().scope`.
 */
export async function getAccessTokenAsync(scope?: string): Promise<string | null> {
  const ssoSession = localStorage.getItem('ssoSession');
  if (ssoSession) {
    try {
      const parsed = JSON.parse(ssoSession) as { claims?: Record<string, unknown> };
      if (parsed?.claims) {
        // Dynamic imports to avoid loading MSAL when not needed.
        // Must use the *initialized* instance — msal-browser v4 throws on
        // getAllAccounts / acquireTokenSilent if initialize() hasn't resolved,
        // which happens on hard page reloads before LoginPage has rendered.
        const { getInitializedMsalInstance } = await import('./msalInstance');
        const { loginRequest } = await import('./msalConfig');
        const msalInstance = await getInitializedMsalInstance();
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          const resolvedScope = scope ?? getConfig().scope;
          const tokenResponse = await msalInstance.acquireTokenSilent({
            ...loginRequest(resolvedScope, accounts[0].username),
            account: accounts[0],
          });
          return tokenResponse.accessToken;
        }
      }
    } catch {
      // MSAL silent acquisition failed — fall through to legacy token below
    }
  }

  // Legacy fallback
  const token = getAccessToken();
  return token ?? null;
}

// ---------------------------------------------------------------------------
// User roles
// ---------------------------------------------------------------------------

/**
 * Retrieve the current user's roles from SSO session or legacy storage.
 */
export function getUserRoles(): string[] {
  const ssoSession = localStorage.getItem('ssoSession');
  if (ssoSession) {
    try {
      const parsed = JSON.parse(ssoSession) as { claims?: { cus_roles?: string | string[] } };
      const cusRoles = parsed?.claims?.cus_roles;
      if (cusRoles) {
        return Array.isArray(cusRoles) ? cusRoles : [cusRoles];
      }
    } catch {
      // Fall through to legacy
    }
  }

  const stored = loadTokensFromStorage();
  if (stored?.identity?.roles) {
    return Array.isArray(stored.identity.roles)
      ? stored.identity.roles
      : [stored.identity.roles];
  }
  return [];
}

// ---------------------------------------------------------------------------
// Identity API endpoints
// ---------------------------------------------------------------------------

const FALLBACK_LOGIN_ERROR =
  'Login failed. Please check your credentials and try again.';

/**
 * Legacy username/password login via Identity API.
 */
export async function loginUser(
  username: string,
  password: string,
): Promise<LoginResult> {
  const client = authClient('apiBaseAuth');
  try {
    const res = await client.post<{
      token: string;
      refreshTokenId: string;
      expiresAtUtc: string | null;
    }>('/login', { username, password });

    const { token, refreshTokenId: refreshId, expiresAtUtc } = res.data;
    const rawToken = normalizeToken(token);

    const identity = await decodeToken(rawToken);
    saveTokensToStorage(rawToken, refreshId, expiresAtUtc, identity);

    return { token: rawToken, refreshTokenId: refreshId, expiresAtUtc, identity };
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response) {
      const status = err.response.status;
      if (status >= 400 && status < 500) {
        const data = err.response.data as Record<string, unknown> | string | undefined;
        let message: string | null = null;
        if (typeof data === 'string') {
          message = data;
        } else if (data && typeof data === 'object') {
          message = (data.message ?? data.title ?? null) as string | null;
        }
        const error: LoginError = new Error(message ?? FALLBACK_LOGIN_ERROR);
        error.status = status;
        throw error;
      }
    }
    throw new Error(FALLBACK_LOGIN_ERROR);
  }
}

/**
 * Refresh the access token using the stored refresh token ID.
 */
export async function refreshAccessToken(): Promise<RefreshResult> {
  const currentRefreshTokenId = getRefreshTokenId();
  if (!currentRefreshTokenId) {
    throw new Error('No refresh token available');
  }

  const client = authClient('apiBaseAuth');
  try {
    const res = await client.post<{
      token: string;
      refreshTokenId: string;
      expiresAtUtc: string | null;
    }>('/refresh-token', { refreshTokenId: currentRefreshTokenId });

    const { token, refreshTokenId: newRefreshId, expiresAtUtc } = res.data;
    saveTokensToStorage(token, newRefreshId, expiresAtUtc);

    return { token, refreshTokenId: newRefreshId, expiresAtUtc };
  } catch (error) {
    clearTokensFromStorage();
    throw error;
  }
}

/**
 * Revoke the current refresh token (logout server-side).
 */
export async function revokeToken(reason = 'User logout'): Promise<void> {
  const currentRefreshTokenId = getRefreshTokenId();
  if (!currentRefreshTokenId) {
    clearTokensFromStorage();
    return;
  }

  const client = authClient('apiBaseAuth');
  try {
    await client.post('/revoke-token', {
      refreshTokenId: currentRefreshTokenId,
      reason,
    });
  } catch {
    // Token revocation failed — continue with logout
  } finally {
    clearTokensFromStorage();
  }
}

/**
 * Call the Identity API `who-am-i` endpoint using a bearer token.
 */
export async function whoAmI(token: string): Promise<WhoAmIResult> {
  const client = authClient('apiBaseAuthV2');
  try {
    const res = await client.post<WhoAmIResult>(
      '/who-am-i',
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
    );
    return res.data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response) {
      throw new Error('Failed to fetch who-am-i');
    }
    throw err;
  }
}

/**
 * Decode a JWT via the Identity API `/decode` endpoint.
 */
export async function decodeToken(token: string): Promise<DecodedIdentity> {
  const client = authClient('apiBaseAuth');
  const tokenToDecode = token.replace('Bearer ', '');

  const response = await client.post<DecodedIdentity>(
    '/decode',
    `"${tokenToDecode}"`,
    { headers: { 'Content-Type': 'application/json' } },
  );

  return response.data;
}

/**
 * Link an SSO account to an existing legacy account via the Identity API.
 */
export async function linkAccount(
  ssoAccessToken: string,
  username: string,
  password: string,
): Promise<void> {
  const client = authClient('apiBaseAuthV2');
  await client.post(
    '/link-account',
    { username, password },
    {
      headers: {
        Authorization: `Bearer ${ssoAccessToken}`,
        'Content-Type': 'application/json',
      },
    },
  );
}
